import { AbstractControl, UntypedFormGroup } from '@angular/forms';

import {
  AnyDynamicInput,
  DynamicDividerConfig,
  DynamicFormLoaderConfig,
  DynamicGroupConfig,
  DynamicInfoConfig,
  DynamicInputConfig,
  DynamicInputConfigType,
  DynamicLabelConfig,
  DynamicMessageConfig,
} from '../../../types';
import { AdminSelectInput } from '../../admin-select-input/admin-select-input.component';
import { AdminsInput } from '../../admins-input/admins-input.component';
import { CandidateEmailInput } from '../../candidate-email-input/candidate-email-input.component';
import { CheckboxInput } from '../../checkbox-input/checkbox-input.component';
import { CountrySelectInput } from '../../country-select-input/country-select-input.component';
import { CustomInput } from '../../custom-input/custom-input.component';
import { DateDropdownsInput } from '../../date-dropdowns-input/date-dropdowns-input.component';
import { DatePickerInput } from '../../date-picker-input/date-picker-input.component';
import { DynamicListInfo } from '../../dynamic-list-info/dynamic-list-info.component';
import { SelectInput } from '../../dynamic-select-input/dynamic-select-input.component';
import { FormLoaderInput } from '../../form-loader/form-loader-input.component';
import { GeneratedPasswordInput } from '../../generated-password-input/generated-password-input.component';
import { PlacementInput } from '../../placement-input/placement-input.component';
import { PrependInputAdapter } from '../../prepend-input-adapter/prepend-input-adapter.component';
import { RegionSelectInput } from '../../region-select-input/region-select-input.component';
import { ResourceSelectInput } from '../../resource-select-input/resource-select-input.component';
import { DynamicSubdomainInput } from '../../subdomain-input-adapter/dynamic-subdomain-input.component';
import { TimePickerInput } from '../../time-picker-input/time-picker-input.component';
import { DynamicFormAdditionalGroups } from '../dynamic-form-additional-groups/dynamic-form-additional-groups';
import { EmailInput, TextInput } from '../dynamic-form-input/dynamic-form-input';
import { TypeaheadInput } from '../dynamic-typeahead-input/dynamic-typeahead-input.component';

export class DynamicFormGroup {
  public readonly kind = <const>'DynamicFormGroup';
  public type: DynamicGroupConfig['type'] = DynamicInputConfigType.GROUP;

  public inputs: AnyDynamicInput[];
  public group: UntypedFormGroup;

  constructor(public config: DynamicGroupConfig) {
    this.inputs = config.inputs.map(buildInput);
    this.group = buildFormGroup(this.inputs);
  }
}

export class DynamicMessage {
  public readonly kind = <const>'DynamicMessage';
  public type: DynamicMessageConfig['type'] = DynamicInputConfigType.MESSAGE;

  constructor(public config: DynamicMessageConfig) {}
}

export class DynamicInfo {
  public readonly kind = <const>'DynamicInfo';
  public type: DynamicInfoConfig['type'] = DynamicInputConfigType.INFO;

  constructor(public config: DynamicInfoConfig) {}
}

export class DynamicLabel {
  public readonly kind = <const>'DynamicLabel';
  public type: DynamicLabelConfig['type'] = DynamicInputConfigType.LABEL;

  constructor(public config: DynamicLabelConfig) {}
}

export class DynamicDivider {
  public readonly kind = <const>'DynamicDivider';
  public type: DynamicDividerConfig['type'] = DynamicInputConfigType.DIVIDER;

  constructor(public config: DynamicDividerConfig) {}
}

export class DynamicFormLoader {
  public readonly kind = <const>'DynamicFormLoader';
  public type: DynamicFormLoaderConfig['type'] = DynamicInputConfigType.FORM_LOADER;

  constructor(public config: DynamicFormLoader) {}
}

// eslint-disable-next-line max-lines-per-function
export const buildInput = (config: DynamicInputConfig) => {
  switch (config.type) {
    case DynamicInputConfigType.ADDITIONAL_GROUPS: {
      return new DynamicFormAdditionalGroups(config);
    }
    case DynamicInputConfigType.GROUP: {
      return new DynamicFormGroup(config);
    }
    case DynamicInputConfigType.TEXT: {
      return new TextInput(config);
    }
    case DynamicInputConfigType.CHECKBOX: {
      return new CheckboxInput(config);
    }
    case DynamicInputConfigType.EMAIL: {
      return new EmailInput(config);
    }
    case DynamicInputConfigType.CANDIDATE_EMAIL: {
      return new CandidateEmailInput(config);
    }
    case DynamicInputConfigType.TYPEAHEAD: {
      return new TypeaheadInput(config);
    }
    case DynamicInputConfigType.GENERATED_PASSWORD: {
      return new GeneratedPasswordInput(config);
    }
    case DynamicInputConfigType.SELECT: {
      return new SelectInput(config);
    }
    case DynamicInputConfigType.DATE_PICKER: {
      return new DatePickerInput(config);
    }
    case DynamicInputConfigType.DATE_DROPDOWNS: {
      return new DateDropdownsInput(config);
    }
    case DynamicInputConfigType.TIME_PICKER: {
      return new TimePickerInput(config);
    }
    case DynamicInputConfigType.ADMINS: {
      return new AdminsInput(config);
    }
    case DynamicInputConfigType.ADMIN_SELECT: {
      return new AdminSelectInput(config);
    }
    case DynamicInputConfigType.CANDIDATE_PLACEMENT: {
      return new PlacementInput(config);
    }
    case DynamicInputConfigType.RESOURCE_SELECT: {
      return new ResourceSelectInput(config);
    }
    case DynamicInputConfigType.MESSAGE: {
      return new DynamicMessage(config);
    }
    case DynamicInputConfigType.INFO: {
      return new DynamicInfo(config);
    }
    case DynamicInputConfigType.LABEL: {
      return new DynamicLabel(config);
    }
    case DynamicInputConfigType.DIVIDER: {
      return new DynamicDivider(config);
    }
    case DynamicInputConfigType.FORM_LOADER: {
      return new FormLoaderInput(config);
    }
    case DynamicInputConfigType.COUNTRY_SELECT: {
      return new CountrySelectInput(config);
    }
    case DynamicInputConfigType.REGION_SELECT: {
      return new RegionSelectInput(config);
    }
    case DynamicInputConfigType.LIST_INFO: {
      return new DynamicListInfo(config);
    }
    case DynamicInputConfigType.SUBDOMAIN: {
      return new DynamicSubdomainInput(config);
    }
    case DynamicInputConfigType.PREPEND: {
      return new PrependInputAdapter(config);
    }
    case DynamicInputConfigType.CUSTOM: {
      return new CustomInput(config);
    }
    default: {
      throw new Error(`Unknown type: ${(<any>config).type}`);
    }
  }
};

export const buildFormGroup = (inputs: AnyDynamicInput[]): UntypedFormGroup =>
  new UntypedFormGroup(
    inputs.reduce(
      (acc, input) => {
        if (input.kind === 'DynamicFormGroup') {
          acc[input.config.name] = input.group;
        } else if (input.kind === 'DynamicFormInput') {
          acc[input.config.name] = input.control;
        }
        return acc;
      },
      <{ [key: string]: AbstractControl }>{},
    ),
  );
